import { useEffect } from 'react'
import { useShallow } from 'zustand/react/shallow'

import type { UserProps } from '@img-arena/consoles-api-dde'
import { DataCard } from '@/components/card'
import { DownloadCsv } from '@/components/DownloadCsv'
import { UserFormButton } from '@/components/forms/UserForm/UserForm'
import {
  ActionDeleteDDEUserItem,
  ActionRedirectItem,
  ActionsMenu
} from '@/components/table/actions'
import { FilterableTable } from '@/components/table/base'
import { CheckboxMenu } from '@/components/table/checkbox'
import { CheckboxDeleteSelectedDDEUserItem } from '@/components/table/checkbox'
import { STORE_STATUS } from '@/constants/store'
import { createBulkHandler } from '@/service/bulkOperationsService'
import { ddeApi } from '@/service/ddeApi'
import { useUsersStoreDDE } from '@/stores/users'

import { DDE_TABLE_COLUMNS, DDE_TABLE_FILTERS } from './users.dde.table'

const usersDDECsvHeaders = ['Username:', 'Email', 'Role', 'Operator', 'Last Active']

const formatUsersDDECsvRow = (row: UserProps) => [
  `${row.userName}`,
  `${row.userId}`,
  `${row.role?.roleName}`,
  `${row.role?.operatorName}`,
  `${row?.lastLogin ?? ''}`
]

const tableId = 'dde-users-table'

export const UsersViewDDE = () => {
  const { data, isFetching, isLoading } = useUsersStoreDDE(
    useShallow((state) => ({
      data: state.data,
      isLoading: state.status === STORE_STATUS.loading,
      isFetching: state.status === STORE_STATUS.fetching
    }))
  )

  const { fetchData, resetState } = useUsersStoreDDE(
    useShallow((state) => ({
      fetchData: state.fetchData,
      resetState: state.resetState
    }))
  )

  useEffect(() => {
    fetchData()
    return resetState
  }, [])

  const handleDeleteUser = async (user: UserProps) => {
    await ddeApi.endpoints.users.delete(user.userId)
    await fetchData()
  }

  const handleDeleteSelectedUsers = createBulkHandler({
    name: 'Delete Users',
    operation: async (user: UserProps) => {
      await ddeApi.endpoints.users.delete(user.userId)
    },
    refreshData: fetchData,
    getItemDisplayName: (user) => `${user.userName} (${user.userId})`
  })

  const handleRedirectUser = (user: UserProps) => {
    return `/operators/${user.role?.operatorId}/users`
  }

  const isRedirectDisabled = (user: UserProps) => {
    return user.role?.roleName !== 'Operator'
  }

  return (
    <DataCard
      title="Users"
      headerPostfixActions={
        <>
          <CheckboxMenu tableId={tableId}>
            <CheckboxDeleteSelectedDDEUserItem onDelete={handleDeleteSelectedUsers} />
          </CheckboxMenu>
          <DownloadCsv
            data={data}
            csvFileName="Users DDE CSV"
            csvHeaders={usersDDECsvHeaders}
            formatCsvRow={formatUsersDDECsvRow}
          />
          <UserFormButton.DDE onSubmit={fetchData} />
        </>
      }
    >
      <FilterableTable
        data={data}
        tableId={tableId}
        loading={isLoading}
        fetching={isFetching}
        filters={DDE_TABLE_FILTERS}
        columns={DDE_TABLE_COLUMNS}
        clientSideSortAndPagination
        clientSideFilters
        withCheckboxes
        rowActions={
          <ActionsMenu>
            <ActionRedirectItem
              redirectTo={handleRedirectUser}
              isDisabled={isRedirectDisabled}
              text="Operator"
              disabledMessage="Option not available for Admin role"
            />
            <ActionDeleteDDEUserItem onDelete={handleDeleteUser} />
          </ActionsMenu>
        }
      />
    </DataCard>
  )
}
